/**
 * apiAdapter — Fuel Stock & Issue Report
 *
 * The report is maintained in the AIM project (`voice-ui-demo`), where it calls
 * AssetPro through that project's `utils/apiService.js` — a bare `fetch` wrapper
 * with its own auth headers. This app already has one configured axios instance
 * (`src/util/Api.js`) that owns the base URL per environment, attaches the
 * Authorization header and routes 401s through the app's session handling.
 *
 * Rather than edit ~2,200 lines of call sites (and re-edit them on every re-sync
 * with AIM), the report keeps its `apiGet` / `apiPost` calls and this adapter maps
 * them onto the app's client. Same approach as
 * `src/containers/AssetReclassification/apiAdapter.js`.
 *
 * The contract the report was written against must be reproduced exactly:
 * AIM's wrapper returns **`{ success, data }`**, not the parsed body. The report
 * reads `bowserRes.data`, `mileageRes.data` and `extractList(res)` → `res.data`,
 * and branches on `res.success === false`.
 *
 * Returning the body directly instead is silent: `res.success` is undefined
 * (which is not `=== false`, so no error is raised) and `res.data` is undefined,
 * so every list comes back empty and the report says "No storage tanks / bowsers
 * were returned for this period" on an organisation that has two of them.
 */

import http from '../../../util/Api'

const path = (endpoint) => String(endpoint || '').replace(/^\/+/, '')

// headers must always be an object: the app's request interceptor assigns
// `config.headers.Authorization`, which throws on an undefined headers bag when
// a call site passes no options at all (apiPost('/fuel/bowser-report', payload)).
const cfg = (options = {}) => ({
  headers: { ...(options.headers || {}) },
  ...(options.params ? { params: options.params } : {})
})

// Errors are RETURNED as { success: false, error }, matching AIM: the report
// checks `res.success === false` and surfaces `res.error` in its own alert.
const fail = (error) => {
  const res = error && error.response
  return {
    success: false,
    error: (res && res.data && (res.data.message || res.data.error))
      || (error && error.message)
      || 'Request failed'
  }
}

export const apiGet = async (endpoint, options = {}) => {
  try {
    const res = await http.get(path(endpoint), cfg(options))
    return { success: true, data: res.data }
  } catch (e) { return fail(e) }
}

export const apiPost = async (endpoint, body, options = {}) => {
  try {
    const res = await http.post(path(endpoint), body, cfg(options))
    return { success: true, data: res.data }
  } catch (e) { return fail(e) }
}

export default { apiGet, apiPost }
